import type { Evaluation } from "@/types/evaluation";
import type { TemplateContent } from "@/types/template";
import { getContentCompletion } from "@/utils/completion";

interface ParsedTemplate {
  id: string;
  parsed: TemplateContent;
}

function EvalCompletionBadge({
  ev,
  getParsedContent,
}: Readonly<{ ev: Evaluation; getParsedContent: (id: string) => TemplateContent | undefined }>) {
  const templateContent = getParsedContent(ev.templateId);
  const comp = templateContent
    ? getContentCompletion(
        templateContent.content,
        ev.sectionToggles,
        ev.values,
        ev.subTemplateValues,
      )
    : { completed: 0, total: 0 };
  return (
    <span className="field-count">
      {comp.completed}/{comp.total}
    </span>
  );
}

function MenuEvalsList({
  evaluationCount,
  getParsedContent,
  onNavigate,
  onNewEval,
  recentEvals,
}: Readonly<{
  recentEvals: Evaluation[];
  evaluationCount: number;
  getParsedContent: (id: string) => TemplateContent | undefined;
  onNavigate: (path: string) => void;
  onNewEval: () => void;
}>) {
  return (
    <>
      <div className="menu-section-header">
        <span>Evaluations</span>
        <button onClick={onNewEval}>+ New</button>
      </div>
      <ul className="menu-list">
        <li
          onClick={() => {
            onNavigate("/fill");
          }}
        >
          All Evaluations
          <span className="field-count">{evaluationCount}</span>
        </li>
        {recentEvals.map((ev) => (
          <li
            key={ev.id}
            onClick={() => {
              onNavigate(`/unified/${ev.id}`);
            }}
          >
            <span style={{ fontSize: "12px" }}>{ev.name}</span>
            <EvalCompletionBadge ev={ev} getParsedContent={getParsedContent} />
          </li>
        ))}
      </ul>
    </>
  );
}

function MenuTemplatesList({
  menuTemplates,
  onNavigate,
  onNewTemplate,
  templateCount,
}: Readonly<{
  menuTemplates: ParsedTemplate[];
  templateCount: number;
  onNavigate: (path: string) => void;
  onNewTemplate: () => void;
}>) {
  return (
    <>
      <div className="menu-section-header">
        <span>Templates</span>
        <button onClick={onNewTemplate}>+ New</button>
      </div>
      <ul className="menu-list">
        <li
          onClick={() => {
            onNavigate("/templates");
          }}
        >
          All Templates
          <span className="field-count">{templateCount}</span>
        </li>
        {menuTemplates.map((t) => (
          <li
            key={t.id}
            onClick={() => {
              onNavigate(`/build/${t.id}`);
            }}
          >
            <span>{t.parsed.name}</span>
            {t.parsed.category && (
              <span className="category-badge category-badge-sm">{t.parsed.category}</span>
            )}
          </li>
        ))}
      </ul>
    </>
  );
}

export function HamburgerMenu({
  evaluationCount,
  getParsedContent,
  menuTemplates,
  onClearDb,
  onNavigate,
  onNewEval,
  onNewTemplate,
  recentEvals,
  templateCount,
}: Readonly<{
  recentEvals: Evaluation[];
  evaluationCount: number;
  menuTemplates: ParsedTemplate[];
  templateCount: number;
  getParsedContent: (id: string) => TemplateContent | undefined;
  onNavigate: (path: string) => void;
  onNewEval: () => void;
  onNewTemplate: () => void;
  onClearDb: () => void;
}>) {
  return (
    <div className="hamburger-menu">
      <MenuEvalsList
        recentEvals={recentEvals}
        evaluationCount={evaluationCount}
        getParsedContent={getParsedContent}
        onNavigate={onNavigate}
        onNewEval={onNewEval}
      />
      <MenuTemplatesList
        menuTemplates={menuTemplates}
        templateCount={templateCount}
        onNavigate={onNavigate}
        onNewTemplate={onNewTemplate}
      />
      <div className="menu-footer">
        <button
          onClick={() => {
            globalThis.location.href = "/explainer.html";
          }}
        >
          How Templates Work
        </button>
        <button className="danger" onClick={onClearDb}>
          Clear DB
        </button>
      </div>
    </div>
  );
}
